import { useState } from 'react'
import SubHeaders from '@/components/Headers/SubHeaders'
import { IPermissionProps } from '@/types/shared'
import { paths } from '@/utils/path'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, FormItem, GridInformation } from 'vnetwork-ui'
import { Cv } from '../../types'
import {
    CV_ATTACHMENT_FILE_PREFIX,
    DEPARTMENT,
    TYPES_OF_POSITION,
} from '../../constants'
import { Link, useNavigate } from 'react-router-dom'
import AsyncSelect from '@/components/Form/Shared/AsyncSelect'
import { analyzeCV, createCV, getCvSourcesOptions } from '../../services'
import { getAllJobPublicOptions } from '@/modules/Jobs/services'
import useOpenTour from '@/hooks/useOpenTour'
import useUploadFile from '@/hooks/useUploadFile'
import { renderFileItem } from '@/utils/function'
import { createCvSchema } from './schema'
import { yupResolver } from '@hookform/resolvers/yup'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import AsyncSelectWithServer from '@/components/Form/Shared/AsyncSelectWithServer'
import { CreateCVSourceProps } from '@/modals/ResumeModals/CreateCVSource'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import toast from 'react-hot-toast'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const Create: React.FC<IPermissionProps> = () => {
    useOpenTour()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { setModal, closeModal } = useModal()
    const { refetchCurrent } = useDataGridRefetch({ reactQueryKey: 'resume' })
    const { refetchCurrent: refetchSource } = useDataGridRefetch({
        reactQueryKey: 'cover_letter_source_id',
    })
    const {
        control,
        handleSubmit,
        setValue,
        formState: { isValid },
    } = useForm<Partial<Cv>>({
        mode: 'onChange',
        defaultValues: {
            name: '',
            email: '',
            day_of_birth: null,
            phone: '',
            address: '',
            job_title: '',
            type_of_position: '',
            department: '',
            about: '',
            url: '',
            url_attachment: [],
            cover_letter_source_id: 0,
        },
        //@ts-ignore
        resolver: yupResolver(createCvSchema(t)),
    })
    const [isScanning, setIsScanning] = useState(false)

    // Map analysis result fields onto the CV form. Data-driven so cognitive
    // complexity stays low — each entry: [form field, value extractor].
    const applyAnalysisToForm = (
        result: NonNullable<Awaited<ReturnType<typeof analyzeCV>>['result']>
    ) => {
        const { candidate, profile } = result
        const mappings: [keyof Cv, string | undefined | null][] = [
            ['name', candidate?.name],
            ['email', candidate?.email],
            ['phone', candidate?.phone],
            ['address', candidate?.address],
            ['day_of_birth', candidate?.day_of_birth],
            ['job_title', profile?.title],
            ['about', profile?.summary],
        ]
        mappings.forEach(([field, value]) => {
            if (value) setValue(field, value)
        })
    }

    const handleScanCV = async () => {
        const files = control._formValues.url_attachment
        if (!files?.length) {
            toast.error(t('scan-cv-no-file', { ns: 'resume' }))
            return
        }
        setIsScanning(true)
        try {
            const response = await analyzeCV(files[0])
            if (response?.result) {
                applyAnalysisToForm(response.result)
                toast.success(t('scan-cv-success', { ns: 'resume' }))
            }
        } catch {
            toast.error(t('scan-cv-failed', { ns: 'resume' }))
        } finally {
            setIsScanning(false)
        }
    }

    const { uploadAsync } = useUploadFile({
        prefix: CV_ATTACHMENT_FILE_PREFIX,
    })
    const { mutateAsync: createCvMutation, isPending } = useMutationWithHandler(
        {
            mutationFn: createCV,
        },
        {
            reactQueryKeys: QUERY_KEYS.RESUMES,
            successMessage: t('create-success'),
            onSuccess: () => {
                refetchCurrent()
                navigate('/resume')
            },
        }
    )
    const onSubmit = async (data: Partial<Cv>) => {
        let fileUrl = ''
        let attachment

        try {
            if (data?.url_attachment?.[0]) {
                const uploadResult = await uploadAsync(data.url_attachment)

                if (uploadResult?.[0]?.status !== 'fulfilled') {
                    toast.error(t('upload-file-failed'))
                    return
                }

                fileUrl = uploadResult[0].value.attachment_file_url ?? ''
                attachment = uploadResult[0].value
            }

            const dataToSubmit = {
                payload: {
                    cv: {
                        ...data,
                        url: fileUrl,
                        type_file: 'file',
                    },
                    attachment,
                },
            }
            console.log('dataToSubmit', dataToSubmit)
            await createCvMutation(dataToSubmit)
        } catch (error) {
            console.error('Error submitting form', error)
        }
    }
    const handleNoOptionSource = (value: string) => {
        setModal<CreateCVSourceProps>('ResumeModals/CreateCVSource/index', {
            input: value,
            onSuccessCreateCVSource: (data) => {
                const cover_letter_source_id =
                    data?.result?.cover_letter_source_id
                setValue('cover_letter_source_id', cover_letter_source_id)
                refetchSource()
                closeModal()
            },
        })
    }
    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.talentPoolManager,
                        title: t('resume-list'),
                    },
                ]}
            />
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <GridInformation
                        data={[
                            {
                                label: '',
                                value: (
                                    <div id="url">
                                        <FormItem
                                            control={control}
                                            name="url_attachment"
                                            type="files"
                                            label={t('upload-files')}
                                            renderFileItem={renderFileItem}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={handleScanCV}
                                            loading={isScanning}
                                            disabled={isScanning || isPending}
                                            className="mt-2"
                                        >
                                            {isScanning
                                                ? t('scanning-cv', {
                                                      ns: 'resume',
                                                  })
                                                : t('scan-cv', {
                                                      ns: 'resume',
                                                  })}
                                        </Button>
                                    </div>
                                ),
                                className: 'lg:col-span-4 col-span-1',
                            },
                            {
                                label: '',
                                value: (
                                    <div id="name">
                                        <FormItem
                                            control={control}
                                            name="name"
                                            type="textfield"
                                            label={t('name')}
                                            placeholder={t('name')}
                                        />
                                    </div>
                                ),
                            },
                            {
                                label: '',
                                value: (
                                    <div id="email">
                                        <FormItem
                                            control={control}
                                            name="email"
                                            type="textfield"
                                            label={t('email')}
                                            placeholder={t('email')}
                                        />
                                    </div>
                                ),
                            },
                            {
                                label: '',
                                value: (
                                    <div id="day-of-birthday">
                                        <FormItem
                                            control={control}
                                            name="day_of_birth"
                                            type="datepicker"
                                            label={t('day-of-birthday')}
                                            placeholder={t('day-of-birthday')}
                                        />
                                    </div>
                                ),
                            },
                            {
                                label: '',
                                value: (
                                    <div id="phone">
                                        <FormItem
                                            control={control}
                                            name="phone"
                                            type="textfield"
                                            label={t('uiPhone')}
                                            placeholder={t('uiPhone')}
                                        />
                                    </div>
                                ),
                            },
                            {
                                label: '',
                                value: (
                                    <div id="address">
                                        <FormItem
                                            control={control}
                                            name="address"
                                            type="textarea"
                                            label={t('address-cv')}
                                            placeholder={t('address-cv')}
                                        />
                                    </div>
                                ),
                            },
                            {
                                label: '',
                                value: (
                                    <div id="job-title">
                                        <AsyncSelect
                                            asyncOptions={
                                                getAllJobPublicOptions
                                            }
                                            name="job_title"
                                            control={control}
                                            label={t('position-applied')}
                                            placeholder={t('position-applied')}
                                        />
                                    </div>
                                ),
                            },
                            {
                                label: '',
                                value: (
                                    <div id="cover-letter-source-id">
                                        <AsyncSelectWithServer
                                            asyncOptions={getCvSourcesOptions}
                                            name="cover_letter_source_id"
                                            control={control}
                                            label={t('source')}
                                            placeholder={t('source')}
                                            queryKey={refetchSource}
                                            noOptionHandler={
                                                handleNoOptionSource
                                            }
                                        />
                                    </div>
                                ),
                            },
                            {
                                label: '',
                                value: (
                                    <div id="types-of-position">
                                        <FormItem
                                            control={control}
                                            name="type_of_position"
                                            type="select"
                                            options={TYPES_OF_POSITION}
                                            label={t('types-of-position')}
                                            placeholder={t('types-of-position')}
                                        />
                                    </div>
                                ),
                            },
                            {
                                label: '',
                                value: (
                                    <div id="department">
                                        <FormItem
                                            control={control}
                                            name="department"
                                            type="select"
                                            options={DEPARTMENT}
                                            label={t('department-cv')}
                                        />
                                    </div>
                                ),
                            },
                            {
                                label: '',
                                value: (
                                    <div id="about">
                                        <FormItem
                                            control={control}
                                            name="about"
                                            type="textarea"
                                            label={t('about')}
                                            placeholder={t('about')}
                                        />
                                    </div>
                                ),
                                className: 'lg:col-span-3 col-span-1',
                            },
                        ]}
                        head={{
                            title: t('detailed-information'),
                        }}
                    />
                </form>
                <div className="p-4 flex items-center justify-end gap-4 bg-white rounded-[8px] mt-2">
                    <Link to="/resume">
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={isPending}
                        >
                            {t('cancle-operation')}
                        </Button>
                    </Link>
                    <Button
                        id="create-cv"
                        variant="contained"
                        size="small"
                        type="submit"
                        onClick={handleSubmit(onSubmit)}
                        loading={isPending}
                        disabled={!isValid || isPending}
                    >
                        {t('create-cv')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default Create
